import React, { useEffect, useMemo } from "react";
import { useActivityRows } from "../../hooks/useActivityRows";
import { useExtIdNames } from "../../hooks/useExtIdNames";
import { ActivityStatusTag } from "./ActivityStatusTag";
import { DateRangesValue } from "./DateRangesValue";
import {
    TActivityLinePart,
    TObjectFilter,
    getActivityStatusKey,
    collectExtIds,
} from "./ActivityInfo.utils";
import { Spin } from "@timeedit/ui-components/antd";
import { Button } from "@timeedit/ui-components/components/Button/Button";
import { FilterOutlined } from "@timeedit/ui-components/components/Icon/Icons/index";
import { objectFilterToSearchCriteria } from "../../lib/FilterUtils";
import { useObjectSelectMacros } from "../../hooks/useObjectSelectMacros";
import Language from "../../lib/Language";
import { useAppContext } from "../../contexts/AppContext";
import { TComponent } from "../../types/utils";

type TObjectFilterViewProps = {
    filter: TObjectFilter;
    getFieldName: (extId: string) => string;
    onApply: (filter: TObjectFilter) => void;
};

const ObjectFilterView: TComponent<TObjectFilterViewProps> = ({
    filter,
    getFieldName,
    onApply,
}) => {
    const applyLabel = Language.get("nc_activity_apply_filter");
    return (
        <div className="activityFilter">
            <div>
                {filter?.categories?.map((category, index) => (
                    <div key={index}>
                        <div>{`${getFieldName(category.fieldExtId)}:`}</div>
                        <ul className="activityFilterValues">
                            {category.values.map((value) => (
                                <li key={value}>{value}</li>
                            ))}
                        </ul>
                    </div>
                ))}
                {filter?.searchString ? (
                    <div>
                        <div>{`${Language.get("dynamic_object_list_search")}:`}</div>
                        <ul className="activityFilterValues">
                            <li>{filter.searchString}</li>
                        </ul>
                    </div>
                ) : null}
            </div>
            <Button
                size="small"
                type="text"
                // The pane's text colour rather than antd's, so it shows on dark backgrounds.
                style={{ color: "inherit" }}
                title={applyLabel}
                onClick={() => onApply(filter)}
                icon={<FilterOutlined alt={applyLabel} />}
            />
        </div>
    );
};

type TActivityInfoProps = {
    activityId: string;
    onLoaded?: () => void;
};

const ActivityInfo: TComponent<TActivityInfoProps> = ({ activityId, onLoaded }) => {
    const { organizationId } = useAppContext();
    const { applySearchCriteria } = useObjectSelectMacros("activityInfo");
    const { activity, rows, isLoading, isError } = useActivityRows(organizationId, activityId);
    const extIds = useMemo(() => collectExtIds(rows), [rows]);
    const getName = useExtIdNames(extIds);
    const renderPart = (part: TActivityLinePart) =>
        "text" in part ? part.text : getName(part.extId, part.kind);

    // Selects the filter's type in the selection pane.
    const applyFilter = (filter: TObjectFilter) =>
        applySearchCriteria(objectFilterToSearchCriteria(filter));

    useEffect(() => {
        if (!isLoading) {
            onLoaded?.();
        }
    }, [isLoading, onLoaded]);

    if (isLoading) {
        return <Spin size="small" />;
    }
    if (isError || !activity) {
        return <div>{Language.get("nc_activity_load_failed")}</div>;
    }

    const statusKey = getActivityStatusKey(activity.activityStatus);

    return (
        <table className="activityInfo">
            <tbody>
                <tr>
                    <th>{Language.get("nc_activity_id")}</th>
                    <td>{activityId}</td>
                </tr>
                <tr>
                    <th>{Language.get("nc_activity_status")}</th>
                    <td>
                        <ActivityStatusTag
                            status={activity.activityStatus}
                            label={statusKey ? Language.get(statusKey) : undefined}
                        />
                    </td>
                </tr>
                {rows.map((row) => (
                    <tr key={row.key}>
                        <th>{renderPart(row.label)}</th>
                        <td>
                            {row.lines.map((line, index) => (
                                <div key={index}>{line.map(renderPart).join("")}</div>
                            ))}
                            {row.dateRange ? <DateRangesValue {...row.dateRange} /> : null}
                            {row.filters.map((filter, index) => (
                                <ObjectFilterView
                                    key={index}
                                    filter={filter}
                                    getFieldName={(extId) => getName(extId, "field")}
                                    onApply={applyFilter}
                                />
                            ))}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

export default ActivityInfo;
